import { useMemo } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import type { CallSheetMembershipDto } from '@signalvote/shared';
import { useProspectBookSummary, useProspectDetail } from '@/hooks/queries';
import { bookQuery, filterLabel, type BookFilters } from '@/lib/fundraising/bookFilters';
import { ProspectProfile } from './ProspectProfile';
import { ProspectSteps, usePrefetchSteps, useStepKeys, useSteps } from './ProspectSteps';

const BOOK = '/admin/fundraising';
const PERSON = '/admin/fundraising/prospects/$prospectId';

const joined = (separator: string, ...parts: (string | false | null | undefined)[]) =>
  parts.filter((part): part is string => !!part).join(separator);

/**
 * One Prospect as a full page, at its own URL — where Match review, the open commitments and other screens
 * link. The book's filters ride along in the URL, so "‹ Prospects" goes back to the same filtered list and
 * J / K step through it. The profile itself is `ProspectProfile`, the same one the book and a call book open
 * over their list.
 */
export function ProspectDetail({ prospectId, filters }: { prospectId: string; filters: BookFilters }) {
  const navigate = useNavigate();
  // The book behind the back link, as its summary: its order is what J / K step through — ids, never its rows.
  const { data: book } = useProspectBookSummary(bookQuery(filters), { withIds: true });
  const ids = useMemo(() => book?.prospectIds ?? [], [book?.prospectIds]);
  // Which of the call books this person is on comes from their page (the profile's own read, shared), so "Add to
  // call book" knows without the book's list of everyone on every book.
  const { data: detail } = useProspectDetail(prospectId);
  const sheets = useMemo<CallSheetMembershipDto[]>(() => {
    const on = new Set((detail?.callBooks ?? []).map((b) => b.id));
    return (book?.sheets ?? []).map(({ id, name, size, concurrencyVersion }) => ({
      id,
      name,
      size,
      concurrencyVersion,
      prospectIds: on.has(id) ? [prospectId] : [],
    }));
  }, [book?.sheets, detail?.callBooks, prospectId]);
  const steps = useSteps(ids, prospectId);
  const goTo = (id: string) => void navigate({ to: PERSON, params: { prospectId: id }, search: filters });
  useStepKeys({ previous: steps.previous, next: steps.next, onStep: goTo });
  usePrefetchSteps(steps);

  const sheetName = filters.sheet ? book?.sheets.find((sheet) => sheet.id === filters.sheet)?.name : undefined;
  const filterCaption = joined(
    ' · ',
    filters.activity && filterLabel(filters.activity),
    filters.match && filterLabel(filters.match),
    sheetName,
    filters.q && `“${filters.q}”`,
  );

  return (
    <div className="min-h-full">
      <div className="flex flex-wrap items-center gap-4 border-b bg-card px-4 py-4 sm:px-8">
        <Link to={BOOK} search={filters} className="text-[13px] font-semibold text-foreground hover:underline">
          ‹ Prospects
        </Link>
        {filterCaption && <span className="text-xs text-muted-foreground">{filterCaption}</span>}
        <div className="ml-auto">
          <ProspectSteps steps={steps} total={book?.total ?? 0} onStep={goTo} />
        </div>
      </div>

      <div className="px-4 py-7 sm:px-8">
        <ProspectProfile
          prospectId={prospectId}
          sheets={sheets}
          onRemoved={() => void navigate({ to: BOOK, search: filters })}
        />
      </div>
    </div>
  );
}
